import React, { createContext, useContext, useState, useEffect } from 'react';
import AlertManager from '../services/AlertManager';

const AlertContext = createContext();

export const useGlobalAlerts = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useGlobalAlerts must be used within an AlertProvider');
    }
    return context;
};

export const AlertProvider = ({ children }) => {
    const [hasAlerts, setHasAlerts] = useState(false);
    const [alertCount, setAlertCount] = useState(0);
    const [alerts, setAlerts] = useState([]);

    // Check for alerts periodically
    const checkForAlerts = async (usageData = null) => {
        try {
            // Get all current alerts from different sources
            const usageAlerts = await AlertManager.getAllAlerts({
                usageData: usageData, // Use provided usage data or default
                tenantId: 'current',
                forceCheck: true
            });

            // You can add other alert sources here (system, account, etc.)
            const allAlerts = [...usageAlerts];

            setAlerts(allAlerts);
            setHasAlerts(allAlerts.length > 0);
            setAlertCount(allAlerts.length);

            return allAlerts;
        } catch (error) {
            console.error('Error checking global alerts:', error);
            return [];
        }
    };

    // Update alert status
    const updateAlertStatus = (newAlerts) => {
        setAlerts(newAlerts);
        setHasAlerts(newAlerts.length > 0);
        setAlertCount(newAlerts.length);
    };

    // Clear all alerts
    const clearAllAlerts = () => {
        setAlerts([]);
        setHasAlerts(false);
        setAlertCount(0);
    };

    useEffect(() => {
        // Initial check
        checkForAlerts();

        // Check every 30 seconds
        const interval = setInterval(checkForAlerts, 30000);

        return () => clearInterval(interval);
    }, []);

    const value = {
        hasAlerts,
        alertCount,
        alerts,
        checkForAlerts,
        updateAlertStatus,
        clearAllAlerts
    };

    return (
        <AlertContext.Provider value={value}>
            {children}
        </AlertContext.Provider>
    );
};

export default AlertContext;
